<template>
  <aside class="sidenav" v-if="showSidenav">
    <div class="sidenav-header">
      <div class="user-info">
        <img
          v-if="companyImageUrl"
          :src="companyImageUrl"
          class="user-logo"
          alt="company logo"
        />
        <div v-else class="user-logo-placeholder">
          <span>{{ companyName ? companyName.charAt(0) : 'R' }}</span>
        </div>
        <div class="user-text">
          <div class="company-name">{{ companyName }}</div>
          <div class="user-name">{{ userName }}</div>
        </div>
      </div>
    </div>

    <SidenavList />

    <div class="sidenav-footer">
      <div class="sidenav-item" @click="signOut">
        <span class="sidenav-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
        </span>
        <span class="sidenav-text">로그아웃</span>
      </div>
    </div>
  </aside>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, Ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { Modal } from 'ant-design-vue';
import { RouterEnum } from '@/router/router.enums';
import apiInstance from '@/axios';
import { logoutRequestConfig } from '@/utils/consoleRefresh';
import {
  endSupportTab,
  getAccessToken,
  getProfile,
  isSupportTab,
} from '@/utils/tokenStore';
import { supportEndedUrl } from '@/utils/supportSession';
import SidenavList from './SidenavList.vue';

export default defineComponent({
  name: 'Sidenav',
  components: {
    SidenavList,
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const userName = ref('');
    const companyName = ref('');
    const companyImageUrl = ref('');

    const showSidenav = computed(() => {
      return !!getAccessToken();
    });

    // 표시용 키는 토큰과 같은 칸(지원 탭이면 그 탭 sessionStorage — tokenStore)
    const loadUserInfo = () => {
      userName.value = getProfile('userNameReseller') || '';
      companyName.value = getProfile('companyNameReseller') || '';
      companyImageUrl.value = getProfile('companyImageUrlReseller') || '';
    };

    const signOut = () => {
      Modal.confirm({
        title: '로그아웃',
        content: '로그아웃 하시겠습니까?',
        okText: '로그아웃',
        cancelText: '취소',
        onOk: async () => {
          // 지원 탭: 그 지원 접속만 끝냄(계정 로그아웃·refresh 쿠키는 건드리지 않음)
          if (isSupportTab()) {
            await apiInstance
              .post('/auth/support/end', null, { showErrorNotice: false })
              .catch(() => undefined);
            endSupportTab('USER');
            window.location.replace(supportEndedUrl('USER'));
            return;
          }
          // 서버가 refresh 쿠키를 지우게 먼저 로그아웃(실패해도 화면 로그아웃은 함 — K-19)
          await apiInstance
            .post('/auth/logout', null, logoutRequestConfig())
            .catch(() => undefined);
          await store.dispatch('clearLocalStorage');
          router.push(RouterEnum.Signin.path);
          window.location.reload();
        },
      });
    };

    // 로그인 상태인데 회사명이 저장되지 않은 경우(이전 버전 최초 로그인 등) 회사 정보를 다시 불러온다
    const refillUserInfo = async (accessToken: string) => {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const response = await apiInstance.get(`/companies/${payload.sub}`);
        const companyData = response.data;
        await store.dispatch('setUserInfo', {
          userName: companyData.accountInfo?.name || '',
          companyName: companyData.name || '',
          companyImageUrl: companyData.imgUrl || '',
        });
        loadUserInfo();
      } catch {
        // 표시용 정보라 실패해도 무시한다
      }
    };

    onMounted(() => {
      loadUserInfo();
      const accessToken = getAccessToken();
      if (accessToken) {
        store.state.showSidenav = true;
        if (!companyName.value) refillUserInfo(accessToken);
      }
    });

    // Store watcher for user info changes
    watch(
      () => store.getters.getUserName,
      (value) => {
        userName.value = value;
      }
    );

    watch(
      () => store.getters.getCompanyName,
      (value) => {
        companyName.value = value;
      }
    );

    watch(
      () => store.getters.getCompanyImageUrl,
      (value) => {
        companyImageUrl.value = value;
      }
    );

    return {
      userName,
      companyName,
      companyImageUrl,
      showSidenav,
      signOut,
    };
  },
});
</script>

<style scoped>
.sidenav {
  position: fixed;
  top: 0;
  left: 0;
  width: 220px;
  height: 100vh;
  background: #1a1a2e;
  display: flex;
  flex-direction: column;
  z-index: 100;
}

.sidenav-header {
  padding: 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.user-logo-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
}

.user-text {
  flex: 1;
  min-width: 0;
}

.company-name {
  font-size: 14px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidenav-footer {
  margin-top: auto;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.sidenav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  color: rgba(255, 255, 255, 0.7);
}

.sidenav-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.sidenav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.sidenav-text {
  font-size: 14px;
}
</style>
